import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toaster } from 'sonner';
import './App.css';
import { api, WS_URL, getToken, setToken } from './api';
import TopBar from './components/TopBar';
import AgentBoard from './components/AgentBoard';
import AdminDashboard from './components/AdminDashboard';
import TVDashboard from './components/TVDashboard';
import Login from './components/Login';
import { Hourglass, LogOut } from 'lucide-react';

function PendingScreen({ user, onLogout, onRefresh }) {
  return (
    <main className="min-h-[calc(100vh-64px)] bg-[#FFFDF6] flex items-center justify-center p-6" data-testid="pending-screen">
      <div className="bg-violet-100 border-2 border-violet-300 rounded-2xl sticky-shadow p-8 max-w-sm text-center rotate-[1deg]">
        <Hourglass size={36} className="mx-auto text-violet-500 mb-3" />
        <h2 className="font-display font-extrabold text-xl text-slate-900">Hi {user.name}! 👋</h2>
        <p className="font-hand text-lg text-violet-800 mt-2">Your login worked, but an admin still needs to give you a role (Agent / Admin / TV).</p>
        <div className="flex justify-center gap-2 mt-5">
          <button data-testid="pending-refresh" onClick={onRefresh}
            className="bg-violet-600 text-white rounded-lg px-4 py-2 text-xs font-bold hover:bg-violet-500">Check again</button>
          <button data-testid="pending-logout" onClick={onLogout}
            className="flex items-center gap-1 bg-slate-200 text-slate-700 rounded-lg px-4 py-2 text-xs font-bold hover:bg-slate-300"><LogOut size={12} /> Logout</button>
        </div>
      </div>
    </main>
  );
}

export default function App() {
  const [user, setUser] = useState(undefined);
  const [agent, setAgent] = useState(null);
  const wsRef = useRef(null);

  const loadMe = useCallback(() => {
    return api.get('/auth/me').then((r) => {
      setUser(r.data);
      setAgent(r.data.agent || null);
    }).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    const logout = () => { setUser(null); setAgent(null); };
    window.addEventListener('snd-logout', logout);
    if (!getToken()) setUser(null);
    else loadMe();
    return () => window.removeEventListener('snd-logout', logout);
  }, [loadMe]);

  useEffect(() => {
    if (user?.role !== 'agent' || !agent) return;
    let stopped = false;
    let ws = null;
    let ping = null;
    const connect = () => {
      ws = new WebSocket(`${WS_URL}?token=${getToken()}`);
      wsRef.current = ws;
      ws.onopen = () => ws.send(JSON.stringify({ type: 'activity', action: 'Opened artboard' }));
      ws.onmessage = (e) => {
        try {
          const m = JSON.parse(e.data);
          if (m.type === 'chat') window.dispatchEvent(new CustomEvent('snd-chat', { detail: m }));
          if (m.type === 'lead-assigned') window.dispatchEvent(new CustomEvent('snd-leads-refresh', { detail: m }));
        } catch {}
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (!stopped) setTimeout(connect, 3000);
      };
    };
    connect();
    ping = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }, 25000);
    let interacted = false;
    const mark = () => { interacted = true; };
    window.addEventListener('mousemove', mark);
    window.addEventListener('keydown', mark);
    window.addEventListener('click', mark);
    const hb = setInterval(() => {
      if (interacted && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'heartbeat', active: true }));
        interacted = false;
      }
    }, 60000);
    return () => {
      stopped = true;
      wsRef.current = null;
      if (ws) ws.close();
      clearInterval(hb);
      clearInterval(ping);
      window.removeEventListener('mousemove', mark);
      window.removeEventListener('keydown', mark);
      window.removeEventListener('click', mark);
    };
  }, [user, agent?.id]);

  const sendActivity = useCallback((action) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'activity', action }));
    }
  }, []);

  const toggleAvailability = useCallback(async () => {
    if (!agent) return;
    const next = !(agent.available ?? true);
    const { toast } = await import('sonner');
    await api.post(`/agents/${agent.id}/availability`, { available: next });
    setAgent((prev) => ({ ...prev, available: next }));
    toast[next ? 'success' : 'warning'](next ? 'You are back online — new leads will flow to you' : 'You are offline — no new leads will be assigned');
  }, [agent]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setAgent(null);
  }, []);

  if (user === undefined) {
    return <div className="min-h-screen bg-[#FFFDF6] flex items-center justify-center font-hand text-2xl text-amber-700">Loading your workspace…</div>;
  }
  if (user === null) {
    return (
      <div className="App">
        <Toaster position="top-center" richColors />
        <Login onLogin={(u) => { setUser(u); setAgent(u.agent || null); }} />
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <TopBar user={user} agent={agent} toggleAvailability={toggleAvailability} onLogout={logout} />
      {user.role === 'agent' && agent ? (
        <AgentBoard key={agent.id} agent={agent} sendActivity={sendActivity} />
      ) : user.role === 'admin' ? (
        <AdminDashboard />
      ) : user.role === 'tv' ? (
        <TVDashboard />
      ) : (
        <PendingScreen user={user} onLogout={logout} onRefresh={loadMe} />
      )}
    </div>
  );
}
